import { Box, Button, FormControl, HStack, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Stack, Text, useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { adminCreateTag, adminDeleteTag, adminGetKeyword } from "../../../api";
import MyIcon from "../../../components/Icon/MyIcon";
import { colors } from "../../../styles";
import { IAdminKeywordDetail } from "../../../types";

interface AddTagModalProps {
    isOpen: boolean;
    onClose: () => void;

    keywordId: string;
    keyword: string;

    page: number;
    size: number;
    searchKeyword: string | null;
}

export default function AddTagModal(props: AddTagModalProps) {
    
    /* query */

    const { data: keywordData } = useQuery<IAdminKeywordDetail>({
        queryKey: ['adminGetKeyword', props.keywordId],
        queryFn: () => adminGetKeyword({ keywordId: props.keywordId }),
        enabled: !!props.keywordId && props.isOpen,
    });

    const tags = useMemo(() => keywordData?.keywordWithMedicines.tags ?? [], [keywordData]);

    /* form */

    interface ICreateTagForm {
        tag: string;
    }

    const { register, handleSubmit, formState: { errors, isValid }, watch, reset } = useForm<ICreateTagForm>({
        mode: "onChange",
    });

    const isAddTagValid = useMemo(() => {
        if (!isValid) return false;

        const tagInputValue = watch("tag");
        if (tagInputValue === undefined || tagInputValue === null || tagInputValue === "") return false;

        if (tags.some((tag) => tag.tag === tagInputValue)) return false;

        return true;
    }, [isValid, watch("tag"), tags]);

    /* onSubmit */

    const onSubmit = async (data: ICreateTagForm) => {
        await createTagMutation.mutateAsync({ keywordId: props.keywordId, tag: data.tag });
    }

    /* onPressDeleteTagBtn */

    const onPressDeleteTagBtn = async (tagId: string) => {
        await deleteTagMutation.mutateAsync({ keywordId: props.keywordId, tagId });
    }

    /* mutations */

    const queryClient = useQueryClient();
    const toast = useToast();

    const createTagMutation = useMutation({
        mutationFn: adminCreateTag,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetKeyword', props.keywordId] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeywords', props.page, props.size, props.searchKeyword] });
            queryClient.invalidateQueries({ queryKey: ['adminGetConnection'] });
            reset({ tag: "" });
        },
        onError: (error: any) => {
            toast({
                title: `${error.response?.data?.message}`,
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    const deleteTagMutation = useMutation({
        mutationFn: adminDeleteTag,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminGetKeyword', props.keywordId] });
            queryClient.invalidateQueries({ queryKey: ['adminGetKeywords', props.page, props.size, props.searchKeyword] });
            queryClient.invalidateQueries({ queryKey: ['adminGetConnection'] });
        },
        onError: (error: any) => {
            toast({
                title: `${error.response?.data?.message}`,
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
    });

    /* render */

    return (
        <Modal motionPreset="slideInBottom" size={"xl"} isOpen={props.isOpen} onClose={props.onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Text color={colors.blue} fontSize="lg" fontWeight="bold">
                       {props.keyword}
                    </Text>
                    <ModalCloseButton />
                </ModalHeader>
                <ModalBody paddingTop={5}>

                    {/* tag list */}

                    <Stack marginTop={5} width={"100%"} padding={3} justifyContent={"center"} alignItems={"flex-start"}>
                        <Text fontSize={16} fontWeight={400} color={colors.black}>{tags.length}개의 태그가 연결됨.</Text>
                        <HStack
                            width={"100%"}
                            paddingY={2}
                            justifyContent={"flex-start"}
                            alignItems={"center"}
                            flexWrap={"wrap"}
                            gap={3}
                        >
                            {tags.map((tag) => (
                                <Box key={tag.id} position="relative" display="inline-block">
                                    <Button 
                                        size={"md"} 
                                        variant={"outline"}
                                        borderColor={colors.blue}
                                        marginRight={1}
                                    >
                                        <Text>{tag.tag}</Text>
                                    </Button>
                                    <Box
                                        position="absolute"
                                        top="-5px"
                                        right="-5px"
                                        width="18px"
                                        height="18px"
                                        borderRadius="50%"
                                        backgroundColor={colors.black}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        zIndex={2}
                                        _hover={{ backgroundColor: colors.g1, cursor: "pointer" }}
                                        as="button"
                                        onClick={() => onPressDeleteTagBtn(tag.id)}
                                    >
                                        <MyIcon src={"/svg/delete-line.svg"} alt="trash" width={12} height={12} />
                                    </Box>
                                </Box>
                            ))}
                        </HStack>
                    </Stack>

                    {/* new tag input */}

                    <HStack marginTop={5} width={"100%"} padding={3} justifyContent={"center"} alignItems={"flex-start"}>
                        <FormControl width={"100%"}>
                            <Input 
                                {...register("tag", { required: true, minLength: 1, maxLength: 20 })}
                                type="text" 
                                placeholder="새로운 태그 입력"
                            />
                            {errors.tag?.message ? <Text fontSize={12} color={colors.red} padding={2}>{errors.tag?.message}</Text> : null}
                            <Text fontSize={12} color={colors.g3} padding={2}>1자 이상 20자 이하로 입력해주세요.</Text>
                        </FormControl>
                    </HStack>

                    {/* button */}

                    <Stack marginTop={5} width={"100%"} padding={3} justifyContent={"center"} alignItems={"center"}>
                        <Button 
                            width={"100%"}
                            backgroundColor={colors.blue}
                            color={colors.white}
                            borderRadius={10}
                            _hover={{ bg: colors.blue }}
                            boxShadow="sm"
                            disabled={!isAddTagValid}
                            onClick={handleSubmit(onSubmit)}
                            isLoading={createTagMutation.isPending}
                        >
                            추가하기
                        </Button>
                    </Stack>
                </ModalBody>
            </ModalContent>
        </Modal>           
    );
}
